import { useState } from 'react'
import { Button } from '@/components/ui'
import { api } from '@/services/api'
import { env } from '@/services/env'
import type { PlanTier } from '@/services/types'

interface PaywallModalProps {
  isOpen: boolean
  onClose: () => void
}

const plans: { tier: Exclude<PlanTier, 'free'>; name: string; price: number; features: string[]; highlighted: boolean }[] = [
  {
    tier: 'solo',
    name: 'Solo',
    price: 9,
    features: ['50 posts/month', 'Voice analysis & AI generation', 'Direct posting'],
    highlighted: false,
  },
  {
    tier: 'pro',
    name: 'Pro',
    price: 29,
    features: ['200 posts/month', 'Post scheduling & auto-posting', 'Analytics dashboard'],
    highlighted: true,
  },
  {
    tier: 'agency',
    name: 'Agency',
    price: 79,
    features: ['Unlimited posts', 'Multiple accounts', 'Priority support'],
    highlighted: false,
  },
]

export function PaywallModal({ isOpen, onClose }: PaywallModalProps) {
  const [loadingTier, setLoadingTier] = useState<PlanTier | null>(null)

  if (!isOpen) return null

  const handleSelectPlan = async (tier: Exclude<PlanTier, 'free'>) => {
    setLoadingTier(tier)
    try {
      const frontendUrl = env.frontendUrl
      const response = await api.createCheckoutSession(
        tier,
        `${frontendUrl}/checkout/success`,
        `${frontendUrl}/checkout/cancel`
      )
      window.location.href = response.checkout_url
    } catch {
      // Checkout creation failed — backend may not be ready yet
      setLoadingTier(null)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-primary-bg border border-border w-full max-w-3xl mx-4 p-6">
        <h2 className="text-xl font-medium text-center mb-2">
          You've used all 15 free generations
        </h2>
        <p className="text-secondary-text text-sm text-center mb-8">
          Pick a plan to keep creating content.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {plans.map((plan) => (
            <div
              key={plan.tier}
              className={`border p-4 flex flex-col ${
                plan.highlighted ? 'border-primary-text border-2' : 'border-border'
              }`}
            >
              <h3 className="text-lg font-medium">{plan.name}</h3>
              <div className="mt-2 mb-4">
                <span className="text-3xl font-medium">${plan.price}</span>
                <span className="text-secondary-text text-sm">/mo</span>
              </div>
              <ul className="space-y-2 mb-4 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm">
                    <span className="text-primary-text mt-0.5">&#10003;</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => handleSelectPlan(plan.tier)}
                loading={loadingTier === plan.tier}
                disabled={loadingTier !== null}
                variant={plan.highlighted ? 'primary' : 'secondary'}
              >
                Choose {plan.name}
              </Button>
            </div>
          ))}
        </div>

        <div className="text-center">
          <button
            onClick={onClose}
            className="text-sm text-secondary-text hover:text-primary-text transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  )
}
